import { useEffect, useState } from 'react';
import { useOrdersContext } from '@/contexts/OrderContext';
import { useNavigate } from 'react-router-dom';
import OrdersLargeContainer from '@/components/front-office/containers/OrdersLargeContainer';
import Button from '@/components/Button';
import Cart from '@/components/front-office/orders/Cart';
import Tooltip from '@/components/Tooltips';
import Modal from '@/components/front-office/Modal';
import PictureDiv from '@/components/front-office/orders/PictureDiv';
import { toast } from 'react-toastify';

const Menu = () => {
	const { fetchMenus, setCart, cart, saveCart } = useOrdersContext();
	const [isLoading, setIsLoading] = useState(false);
	const [selectedMenus, setSelectedMenus] = useState([]);
	const [showLocalModal, setShowLocalModal] = useState(false);
	const [canSaveToIDB, setCanSaveToIDB] = useState(false);

	const navigate = useNavigate();

	useEffect(() => {
		if (cart.is_no_limit) {
			if (cart.is_no_limit_type === 'list') {
				navigate('/commande/liste-des-produits', { replace: true });
			} else if (cart.is_no_limit_type === 'category' && cart.is_no_limit_selected_category) {
				navigate(`/commande/${cart.is_no_limit_selected_category.name.toLowerCase().split(' ').join('-').replace(',', '-')}`, { replace: true });
			}
		}
	}, []);

	useEffect(() => {
		if (
			cart?.categories?.filter((step) => step?.extra_products?.length > 0)?.length > 0 ||
			cart?.categories?.filter((step) => step?.selected_products?.length > 0)?.length > 0
		) {
			setShowLocalModal(true);
		}

		// Reset selectedMenus when cart.restaurant_id changes
		if (cart?.restaurant_id) {
			setSelectedMenus(cart.menus); // Set selectedMenus to cart.menus for the current restaurant
		}

		if (cart.restaurant_id && cart.fetched_menus.length === 0) {
			setIsLoading(true);

			const loadMenus = async () => {
				try {
					await fetchMenus(cart.restaurant_id);
				} catch (error) {
					console.error(error);
					toast.error('Une erreur est survenue', {
						position: 'top-center',
						autoClose: 800,
						hideProgressBar: false,
						closeOnClick: true,
						pauseOnHover: false,
						draggable: true,
						progress: undefined,
					});
				}

				// Delay for 1.5 seconds on purpose
				// To make sure the menus have been fetched (or not)
				setTimeout(() => setIsLoading(false), 1500);
			};

			loadMenus();
		}
	}, [cart.restaurant_id]);

	useEffect(() => {
		if (cart.menus.length > 0) {
			setSelectedMenus(cart.menus); // Mettez à jour les menus sélectionnés
		}
	}, [cart.menus]);

	useEffect(() => {
		const selectedMenusWithAddtionalCategories = cart.categories
			.sort((a, b) => a?.priority - b?.priority)
			.filter((category) => category?.is_selected)
			.map((category) => category?.id);

		const selectedMenusWithoutAddtionalCategories = selectedMenus
			.map((menu) => {
				return menu.steps
					.sort((a, b) => a?.priority - b?.priority)
					.map((step) => step?.category_id)
					.flat();
			})
			.flat();

		// Get the ids of categories in the selected menus
		const selectedMenusIds = [
			...new Set(selectedMenusWithAddtionalCategories.concat(selectedMenusWithoutAddtionalCategories)),
		];

		// Implement the switch logic to reorder selectedMenusIds based on priority
		for (let i = 0; i < selectedMenusIds.length - 1; i++) {
			for (let j = i + 1; j < selectedMenusIds.length; j++) {
				const categoryA = cart.categories.find((category) => category?.id === selectedMenusIds[i]);
				const categoryB = cart.categories.find((category) => category?.id === selectedMenusIds[j]);

				if (categoryA?.priority > categoryB?.priority) {
					// Swap positions
					const temp = selectedMenusIds[i];
					selectedMenusIds[i] = selectedMenusIds[j];
					selectedMenusIds[j] = temp;
				}
			}
		}

		setCart((prevState) => {
			const updatedCategories = prevState.categories.map((category) => {
				// Calculate the total step quantity for this category
				const totalStepQuantity = selectedMenus.reduce((acc, menu) => {
					const step = menu.steps.find((s) => s.category_id === category.id);
					return step ? acc + step.quantity * menu.menu_quantity : acc;
				}, 0);

				// Calculate the total menu quantity for this category
				const totalMenuQuantity = selectedMenus.reduce((acc, menu) => {
					const step = menu.steps.find((s) => s.category_id === category.id);
					return step ? acc + menu.menu_quantity : acc;
				}, 0);

				return {
					...category,
					step_quantity: totalStepQuantity,
					menu_quantity: totalMenuQuantity,
					is_selected:
						selectedMenusIds.includes(category?.id) &&
						selectedMenus.some((menu) => menu.steps.some((step) => step.category_id === category?.id)),
					is_additional: !selectedMenus.some((menu) => menu.steps.some((step) => step.category_id === category?.id)),
				};
			});

			// Update the cart with the selected menus and updated categories
			return {
				...prevState,
				menus: selectedMenus.sort((a, b) => a.steps[0]?.priority - b.steps[0]?.priority),
				categories: updatedCategories,
				currentStepNb: updatedCategories.findIndex((category) => selectedMenusIds.includes(category.id)) ?? 0,
			};
		});

		setCanSaveToIDB(true);
	}, [selectedMenus, setCart]);

	useEffect(() => {
		if (canSaveToIDB) {
			const saveCartInIndexedDB = async () => {
				await saveCart(cart.restaurant_id, cart);
			};

			saveCartInIndexedDB();
		}

		setCanSaveToIDB(false);
	}, [canSaveToIDB]);

	const handleAddProduct = (product, menu) => {
		const hallotInitialQuantity = menu.steps.find((step) => step.category_id === product.category_id)?.quantity;

		setCart((prevCart) => {
			return {
				...prevCart,
				categories: prevCart.categories.map((category) => {
					if (category.id === product.category_id) {
						const existingProductIndex = category.selected_products?.findIndex((p) => p.product_id === product.product_id);
						let updatedSelectedProducts;

						if (existingProductIndex !== -1) {
							updatedSelectedProducts = category.selected_products?.map((p, index) =>
								index === existingProductIndex
									? { ...p, selected_quantity: category.step_quantity + hallotInitialQuantity }
									: p
							);
						} else {
							updatedSelectedProducts = [
								...category.selected_products,
								{ ...product, selected_quantity: hallotInitialQuantity },
							];
						}

						return {
							...category,
							selected_products: updatedSelectedProducts,
							is_done: true,
						};
					}

					return category;
				}),
			};
		});
	};

	const handleRemoveProduct = (product) => {
		setCart((prevCart) => {
			return {
				...prevCart,
				categories: prevCart.categories.map((category) => {
					if (category.id === product.category_id) {
						const productInSelectedProducts = category.selected_products?.find((p) => p.product_id === product.product_id);

						let updatedSelectedProducts = category.selected_products;
						let updatedExtraProducts = category.extra_products;

						if (productInSelectedProducts) {
							const indexSelected = category.selected_products.findIndex((p) => p.product_id === product.product_id);
							const hallotInitialQuantity = prevCart.menus
								.find((menu) => selectedMenus.find((sMenu) => sMenu.id === menu.id))
								.steps.find((step) => step.category_id === product.category_id)?.quantity;

							if (indexSelected !== -1) {
								updatedSelectedProducts = [...category.selected_products];
								updatedSelectedProducts[indexSelected].selected_quantity -= hallotInitialQuantity;

								if (updatedSelectedProducts[indexSelected].selected_quantity <= 0) {
									updatedSelectedProducts.splice(indexSelected, 1);
								}
							}
						}

						const isDoneCondition =
							category.is_done && updatedSelectedProducts.length === 0 && updatedExtraProducts.length === 0;

						return {
							...category,
							selected_products: updatedSelectedProducts,
							is_done: isDoneCondition,
						};
					}
					return category;
				}),
			};
		});
	};

	const updateAndAddMenuToCart = (prevSelectedMenus, menu, action) => {
		const existingMenuIndex = prevSelectedMenus.findIndex((item) => item.id === menu.id);
		const hallotsStep = menu.steps.find((step) => step?.product?.name === 'Hallots');

		if (hallotsStep) {
			if (action === 'add') {
				handleAddProduct(hallotsStep.product, menu);
			} else {
				handleRemoveProduct(hallotsStep.product);
			}
		}

		if (existingMenuIndex !== -1) {
			// Menu item already exists, update the quantity
			const updatedMenus = [...prevSelectedMenus];
			const existingMenu = updatedMenus[existingMenuIndex];

			if (action === 'add') {
				existingMenu.menu_quantity += 1;
			} else if (action === 'remove') {
				existingMenu.menu_quantity -= 1;
			}

			if (existingMenu.menu_quantity <= 0) {
				updatedMenus.splice(existingMenuIndex, 1);
			}

			return updatedMenus;
		} else {
			// Add new menu item with a quantity of 1 if action is 'add'
			if (action === 'add') {
				return [...prevSelectedMenus, { ...menu, menu_quantity: 1 }];
			}

			// If action is 'remove' and the item does not exist, just return the previous state
			return prevSelectedMenus;
		}
	};

	const handleUpdateMenu = (menu, action) => {
		// Vérifier la compatibilité des menus
		if (action === 'add') {
			const hasRoyalMenu = cart.menus.some(m => m.id === 'AIOhlOzPiglhCq7H8xRy');
			const isCurrentMenuRoyal = menu.id === 'AIOhlOzPiglhCq7H8xRy';

			if (hasRoyalMenu && !isCurrentMenuRoyal) {
				toast.error('La Formule Royal ne peut pas être mélangée avec d\'autres formules', {
					position: 'top-center',
					autoClose: 3000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					theme: 'colored',
				});
				return;
			}

			if (!hasRoyalMenu && isCurrentMenuRoyal && cart.menus.length > 0) {
				toast.error('Les autres formules ne peuvent pas être mélangées avec la Formule Royal', {
					position: 'top-center',
					autoClose: 3000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					theme: 'colored',
				});
				return;
			}
		}

		setSelectedMenus((prevSelectedMenus) => updateAndAddMenuToCart(prevSelectedMenus, menu, action));
	};

	const handleToggleModal = () => {
		setShowLocalModal(!showLocalModal);
		navigate('/commande/menu/categories');
	};

	const handleSubmitModal = () => {
		setSelectedMenus([]);

		setCart((prevState) => ({
			...prevState,
			menus: [],
			categories: [
				...prevState.categories.map((category) => {
					return {
						...category,
						selected_products: [],
						extra_products: [],
						menu_quantity: 0,
						step_quantity: 0,
						is_selected: false,
						is_additional: false,
						showModal: false,
						is_done: false,
					};
				}),
			],
		}));

		setShowLocalModal(!showLocalModal);
	};

	return (
		<OrdersLargeContainer>
			<div className="col-span-3 bg-white/90 rounded-3xl shadow-2xl p-4 text-center lg:col-span-2">
				{showLocalModal && (
					<Modal
						showLocalModal={showLocalModal}
						setShowLocalModal={setShowLocalModal}
						withHeader={false}
						handleCancelModalButton={handleToggleModal}
						handleSubmit={handleSubmitModal}
						canCloseModal={!showLocalModal}
					>
						<p>Votre panier est déjà rempli, si vous confirmez, il sera vidé et vous devrez le refaire.</p>
						<p>Souhaitez-vous continuer ?</p>
					</Modal>
				)}

				<h2 className="border-b-2 border-black/.90 pb-2 text-xl font-bold mb-4 md:text-2xl xl:text-3xl" id="menu-title">
					Sélectionnez votre formule
				</h2>
				{isLoading ? (
					<div className="flex flex-col items-center justify-center">
						<div className="animate-spin font-bold rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
						Chargement des menus
					</div>
				) : (
					<div
						id="sidebar-content"
						className={`p-2 grid gap-4 md:gap-2 items-start justify-center overflow-x-hidden overflow-y-auto grid-cols-2 ${window.innerHeight > 568 ? 'h-[calc(100dvh-380px)]' : 'h-[calc(100vh-400px)]'} md:h-[calc(100vh-500px)] md:items-center lg:h-[calc(100vh-400px)] md:landscape:gap-8 ${window.innerHeight <= 800 && window.innerWidth <= 1440 ? 'xl:grid-cols-2' : 'xl:grid-cols-3'}`}
					>
						{cart.fetched_menus?.length > 0 ? (
							cart.fetched_menus
								.sort((a, b) => parseFloat(a.price) - parseFloat(b.price))
								.map((menu, index) => (
									<div key={menu.id || index} className={`grid ${cart.fetched_menus.length > 1 ? 'self-end lg:self-end' : ' self-start lg:self-start'} lg:grid-cols-3 lg:flex lg:items-end`}>
										{/* Left side */}
										<div className="relative mx-auto mb-2 md:mb-0 lg:mx-0">
											{/* Cart quantity */}
											{
												(
													(
														selectedMenus.length > 0 &&
														selectedMenus.find((item) => item.id === menu.id)?.menu_quantity > 0
													) ||
													(
														cart?.menus?.length > 0 &&
														cart?.menus?.find((item) => item.id === menu.id)?.menu_quantity > 0
													)
												) && (
													<div className="text-white absolute top-0 left-0 -translate-x-1/3 -translate-y-1/3 w-6 h-6 bg-primary rounded-2xl z-10 text-xs flex justify-center items-center">
														{cart?.menus?.find((item) => item.id === menu.id)?.menu_quantity ||
															selectedMenus.find((item) => item.id === menu.id)?.menu_quantity ||
															0}
													</div>
												)
											}

											<PictureDiv path={menu.picture} name={menu.name} alt={menu.name} />

											<div
												className={`absolute top-0 right-0 bottom-0 left-0 w-full h-full  ${selectedMenus.find((item) => item.id === menu.id) ? 'bg-black/50' : 'opacity-0 hover:opacity-100'
													} transition duration-300 ease-in-out bg-black/50 rounded-2xl`}
											>
												<div className="h-full text-white flex justify-center items-center gap-4">
													<button
														className="bg-primary rounded-full w-6 h-6"
														onClick={() => handleUpdateMenu(menu, 'remove')}
													>
														-
													</button>
													<p>
														{cart?.menus?.find((item) => item.id === menu.id)?.menu_quantity ||
															selectedMenus.find((item) => item.id === menu.id)?.menu_quantity ||
															0}
													</p>
													<button className="bg-primary rounded-full w-6 h-6" onClick={() => handleUpdateMenu(menu, 'add')}>
														+
													</button>
												</div>
											</div>
										</div>

										{/* Right side */}
										<div className={`${window.innerHeight > 568 ? 'w-[150px]' : 'w-[200px]'} mx-auto text-primary leading-none grid grid-cols-1 text-center md:w-[250px] lg:mr-0 lg:ml-4 lg:text-left`}>
											<h2 className="text-sm leading-none font-bold items-center self-end mb-2 lg:mb-0 lg:w-[120px]">
												<Tooltip data={menu} field="description">
													{menu.name}
												</Tooltip>
											</h2>
											<p className="text-sm">{menu.price}€</p>
											<p className="text-sm">{menu.menu_quantity}</p>

											<Button
												disabled={!false}
												action={null}
												handleClick={() => handleUpdateMenu(menu, 'add')}
												text="Ajouter"
												extraClasses="mt-2 min-w-[80px]"
											/>
										</div>
									</div>
								))
						) : (
							<div className="text-center">
								<p>Aucune formule disponible</p>
							</div>
						)}
					</div>
				)}
				<div className='lg:hidden'>
					<Cart showOnlyButton={true} buttonRoute={`/commande/menu/categories`} />
				</div>
			</div>

			<div className='hidden lg:block'>
				<Cart cart={cart} disabled={cart?.menus?.length > 0} buttonText="Commander" buttonRoute={`/commande/menu/categories`} />
			</div>
		</OrdersLargeContainer>
	);
};

export default Menu;
